import React, { useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  GlobalTimeslots,
  CompanyModuleEnum,
  OpeningEntityTypeEnum,
  ShipmentStateCodeEnum,
  SnPartyConfigDTO,
  TransportationTypeEnum,
} from '@api/ringil-api.v2';
import { Form, proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { findValidationMessage } from '@utils/validation';
import InputTransportationType from '@components/obsolete/Form/InputTransportationType/InputTransportationType.tsx';
import { RC, RR } from '@components/obsolete/RC.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InputInheriterCompany from '@components/obsolete/Form/InputEntityOwner/InputInheriterCompany.tsx';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation.tsx';
import ManageTimeslot from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import InputEntityOwner from '@components/obsolete/Form/InputEntityOwner/InputEntityOwner.tsx';
import InputShipmentStatus from '@components/obsolete/Form/InputShipmentStatus/InputShipmentStatus.tsx';

export interface SupplierNotificationHeaderData {
  transportationType: TransportationTypeEnum;
  companyId: string;
  supplierId: string;
  supplierLocationId: string;
  customerLocationId: string;
  customerLocation?: { timezoneId?: string; countryCode3?: string };
  supplierLocation?: { timezoneId?: string; countryCode3?: string };
  shipmentStateCode: ShipmentStateCodeEnum;
  companyCode?: string;
  supplierDeliverySlots: GlobalTimeslots;
  customerDeliverySlots: GlobalTimeslots;
}

interface ComponentProps {
  form: Form<SupplierNotificationHeaderData>;
  configuration: SnPartyConfigDTO;
  showTimeslots: boolean;
  disabled?: boolean;
}

const SupplierNotificationHeaderForm: React.FC<ComponentProps> = ({ form, configuration, showTimeslots, disabled = false }) => {
  const { tr } = useTranslationLgs();

  const names = proxiedPropertiesOf<SupplierNotificationHeaderData>();

  useEffect(() => {
    if (form.values.supplierLocation?.timezoneId && form.values.supplierLocation.timezoneId !== form.values.supplierDeliverySlots?.timezoneId) {
      form.setFieldValue(names.supplierDeliverySlots, {
        ...form.values.supplierDeliverySlots,
        timezoneId: form.values.supplierLocation.timezoneId,
      });
    }
    if (form.values.customerLocation?.timezoneId && form.values.customerLocation.timezoneId !== form.values.customerDeliverySlots?.timezoneId) {
      form.setFieldValue(names.customerDeliverySlots, {
        ...form.values.customerDeliverySlots,
        timezoneId: form.values.customerLocation.timezoneId,
      });
    }
  }, [form.values.supplierLocation?.timezoneId, form.values.customerLocation?.timezoneId, form, names]);

  return (
    <>
      <InputTransportationType
        code={form.values.transportationType}
        onChange={code => form.setFieldValue('transportationType', code)}
        disabled={disabled}
        singleOption={false}
        dataTestIdPrefix={'sn-header-transportationType'}
      />
      <RR>
        <RC large={6} medium={6} small={6}>
          {(configuration?.supplierAndCustomer?.visible ?? true) && (
            <HorizontalFieldValue
              label={tr(`CreateSupplierNotification.supplier`, `Supplier`)}
              required={true}
              validationError={findValidationMessage(form.validationErrors, 'supplierId', tr)}
              value={
                <>
                  <InputInheriterCompany
                    companyId={form.values.companyId}
                    inheriterId={form.values.supplierId}
                    onChange={v => form.setFieldValue('supplierId', v)}
                    module={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                    style={{ width: '100%' }}
                    readonly={configuration?.supplierAndCustomer?.editable === false || disabled}
                    filterEnabled={true}
                    dataTestId={'sn-header-consignor'}
                  />
                </>
              }
            />
          )}

           <HorizontalFieldValue
             label={tr(`CreateSupplierNotification.supplierLocation`, `Supplier location`)}
             value={
               <InputCompanyLocation
                 moduleName={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                 companyId={form.values.supplierId}
                 companyLocationId={form.values.supplierLocationId}
                 onChange={v => {
                    form.setFieldValue('supplierLocationId', v?.id);
                    form.setFieldValue('supplierLocation', v);
                    if (v?.timezoneId) {
                      form.setFieldValue(names.supplierDeliverySlots, {
                        ...(form.values.supplierDeliverySlots || {}),
                        timezoneId: v.timezoneId,
                      });
                    }
                  }}
                 style={{ width: '100%' }}
                 disabled={disabled}
                 filterPartnerLocation={true}
                 filterEnabled={true}
                 dataTestId={'sn-header-consignorLocation'}
               />
             }
             validationError={findValidationMessage(form.validationErrors, 'supplierLocation', tr)}
             required={true}
            />
           {configuration?.timeslots?.visible && showTimeslots && (
            <>
              <HorizontalFieldValue
                label={tr(`CreateSupplierNotification.supplierTimeslot`, `Supplier timeslot`)}
                required={configuration?.timeslots?.required === true}
                value={
                  <ManageTimeslot
                    entityType={OpeningEntityTypeEnum.LOCATION}
                    entityId={form.values.supplierLocationId}
                    onChange={v => {
                      form.setFieldValue(names.supplierDeliverySlots, { ...v, timezoneId: form.values.supplierLocation?.timezoneId });
                    }}
                    timeslot={form.values.supplierDeliverySlots}
                    validationErrors={form.validationErrors}
                    validationPrefix={'supplierDeliverySlots.'}
                    dataTestId={'sn-header-consignorTimeslot'}
                  />
                }
              />
            </>
          )}
        </RC>
        <RC large={6} medium={6} small={6}>
          {(configuration?.supplierAndCustomer?.visible ?? true) && (
            <HorizontalFieldValue
              required={configuration?.supplierAndCustomer?.required === true}
              label={tr(`CreateSupplierNotification.customer`, `Customer`)}
              value={
                <InputEntityOwner
                  companyId={form.values.companyId}
                  onChange={c => form.setFieldValue('companyId', c)}
                  moduleType={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                  style={{ width: '100%' }}
                  onChangeCompanyCode={v => form.setFieldValue('companyCode', v)}
                  readonly={configuration?.supplierAndCustomer?.editable === false || disabled}
                  filterEnabled={true}
                  dataTestId={'sn-header-consignee'}
                />
              }
            />
          )}

           <HorizontalFieldValue
             label={tr(`CreateSupplierNotification.customerLocation`, `Customer location`)}
             value={
               <InputCompanyLocation
                 moduleName={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                 companyId={form.values.companyId}
                 companyLocationId={form.values.customerLocationId}
                 onChange={v => {
                    form.setFieldValue('customerLocationId', v?.id);
                    form.setFieldValue('customerLocation', v);
                    if (v?.timezoneId) {
                      form.setFieldValue(names.customerDeliverySlots, {
                        ...(form.values.customerDeliverySlots || {}),
                        timezoneId: v.timezoneId,
                      });
                    }
                  }}
                 style={{ width: '100%' }}
                 disabled={disabled}
                 filterPartnerLocation={true}
                 filterEnabled={true}
                 dataTestId={'sn-header-consigneeLocation'}
               />
             }
             required={true}
             validationError={findValidationMessage(form.validationErrors, 'customerLocation', tr)}
            />
           {configuration?.timeslots?.visible && showTimeslots && (
            <HorizontalFieldValue
              label={tr(`CreateSupplierNotification.customerTimeslot`, `Customer timeslot`)}
              value={
                <>
                  <ManageTimeslot
                    entityType={OpeningEntityTypeEnum.LOCATION}
                    entityId={form.values.customerLocationId}
                    onChange={v => {
                      form.setFieldValue(names.customerDeliverySlots, { ...v, timezoneId: form.values.customerLocation?.timezoneId });
                    }}
                    timeslot={form.values.customerDeliverySlots}
                    validationErrors={form.validationErrors}
                    validationPrefix={'customerDeliverySlots.'}
                    dataTestId={'sn-header-consigneeTimeslot'}
                  />
                </>
              }
            />
          )}
        </RC>
      </RR>
      <HorizontalFieldValue
        label={tr('CreateSupplierNotification.shipmentState', 'Shipment state')}
        value={
          <InputShipmentStatus
            onChange={v => form.setFieldValue('shipmentStateCode', v)}
            code={form.values.shipmentStateCode}
            required={true}
            disabled={disabled}
            dataTestIdPrefix={'sn-header-shipmentStatus'}
          />
        }
      />
    </>
  );
};

export default SupplierNotificationHeaderForm;
